import { ReactNode, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Menu, X, Sun, Moon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

export interface PortalShellNavItem {
  label: ReactNode;
  href: string;
  icon: LucideIcon;
  isActive?: (_pathname: string) => boolean;
  external?: boolean;
  target?: '_blank' | '_self';
}

interface PortalShellProps {
  navigation: PortalShellNavItem[];
  logoHref: string;
  children: ReactNode;
  headerLeft?: ReactNode;
  headerCenter?: ReactNode;
  headerRight?: ReactNode;
  sidebarFooter?: ReactNode;
  showThemeToggle?: boolean;
  logo: ReactNode;
}

export function PortalShell({
  navigation,
  logoHref,
  children,
  headerLeft,
  headerCenter,
  headerRight,
  sidebarFooter,
  showThemeToggle = true,
  logo,
}: PortalShellProps) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>('light');

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme || (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    updateTheme(initialTheme);
  }, []);

  const updateTheme = (newTheme: 'light' | 'dark') => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const handleThemeToggle = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    updateTheme(newTheme);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 transform bg-card transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex h-16 items-center justify-between border-b px-6">
            <Link to={logoHref} onClick={() => setSidebarOpen(false)}>
              {logo}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => {
              const isExternal = item.external ?? /^https?:\/\//i.test(item.href);
              const matches = !isExternal
                ? item.isActive
                  ? item.isActive(location.pathname)
                  : location.pathname.startsWith(item.href)
                : false;
              const linkClassName = cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                matches
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              );

              if (isExternal) {
                const target = item.target ?? '_blank';
                const rel = target === '_blank' ? 'noreferrer' : undefined;
                return (
                  <a
                    key={String(item.href)}
                    href={item.href}
                    className={linkClassName}
                    target={target}
                    rel={rel}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <item.icon className="h-5 w-5" />
                    {item.label}
                  </a>
                );
              }

              return (
                <Link
                  key={String(item.href)}
                  to={item.href}
                  className={linkClassName}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {sidebarFooter ? (
            <div className="border-t px-6 py-4">
              {sidebarFooter}
            </div>
          ) : null}
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          {headerLeft ? <div className="flex items-center gap-3">{headerLeft}</div> : null}
          <div className="flex-1 min-w-0">{headerCenter}</div>
          <div className="flex items-center gap-3">
            {showThemeToggle ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleThemeToggle}
                className="h-8 w-8"
              >
                {theme === 'light' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
            ) : null}
            {headerRight}
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
